// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { AltoIcon, Body, Column, InputSelect, XxlPadding, XxsSpacing } from '@alto/design-system';
import React from 'react';
import { CONTACT_US_ORIGIN } from '~shared/constants';
import { type QuantityOption } from '~shared/types/clients';
import { ContactUs } from '../../alto-assistant';
import { getIconNameForDosageForm } from '../helpers/getIconNameForDosageForm';

export type Props = {
  readonly options: QuantityOption[];
  readonly onChange: (option: QuantityOption) => void;
  readonly selectedOption?: QuantityOption;
  readonly friendlyDosageForm: string;
};

const QuantityChangeNotAvailable = () => (
  <XxlPadding>
    <Column centerHorizontally>
      <Body center>
        Quantity change for this medication is not available through the app. Please{' '}
        <ContactUs
          type="link"
          method="message"
          origin={CONTACT_US_ORIGIN.ChangeQuantityList}
        />{' '}
        to change the quantity of this prescription.
      </Body>
    </Column>
  </XxlPadding>
);

export const QuantityChangeSelect = ({ options, onChange, selectedOption, friendlyDosageForm }: Props) => {
  const inputOptions = options.map(({ label, quantity, product_count: productCount }: QuantityOption) => {
    const iconName = getIconNameForDosageForm(friendlyDosageForm);
    return {
      label,
      radioPlacement: 'left' as const,
      value: quantity.toString(),
      RightContent: iconName ? (
        <>
          <AltoIcon
            type="secondary"
            name={iconName}
          />
          <XxsSpacing />
          {!!productCount && (
            <Body
              color={COLORS.TEXT_COLORS.SECONDARY}
              fontFamily="semibold"
              textSize="large"
            >{`x ${productCount}`}</Body>
          )}
        </>
      ) : undefined,
    };
  });

  if (inputOptions.length === 0) return <QuantityChangeNotAvailable />;

  return (
    <InputSelect
      required
      placeholder="Please select new quantity"
      label="Select quantity"
      options={inputOptions}
      value={selectedOption?.quantity?.toString()}
      onValueChange={(value) => {
        const option = options.find((option) => option.quantity === Number(value));
        if (option) {
          onChange(option);
        }
      }}
    />
  );
};
